import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Form, Row, Col, Spinner, Modal, Dropdown } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import './Expenses.scss';
import {
    getExpenses, createExpense, updateExpense, deleteExpense, bulkDeleteExpenses, getExpenseFileBlob,
    getExpensePayers, getOutOfPocketPayments, setPaymentReimbursed, bulkSetPaymentsReimbursed,
    getExpenseColumnPrefs, updateExpenseColumnPrefs, extractExpensePdfLineItems, extractExpenseReceipt,
    getExpensesAccounting, getExpenseAccountingOptions,
} from '../../../services/ExpensesService';
import { getCurrentUserId, isSuperAdmin } from '../../../services/AuthService';
import { formatDateForLanguage, formatEuroForLanguage } from '../../../utils/i18nHelpers';
import { RECEIPT_ACCEPT, isNativelyDisplayable, isPdfFile, prepareImageForScan, readAsDataUrl } from '../../../utils/receiptImage';
import { ReceiptPreview, ReceiptScanResult } from './ReceiptScanPanels';
import ExpenseAccountingFields, { ACCOUNTING_FILTERS, MissingLine, matchesAccountingFilter, missingText } from './ExpenseAccountingFields';
import SupplierQuickAdd from '../Suppliers/SupplierQuickAdd';
import AutoGrowTextarea from './AutoGrowTextarea';
import ExpenseMultiSelect from './ExpenseMultiSelect';
import ExpenseCategorySummary, { SLICE_COLORS } from './ExpenseCategorySummary';
import { useRompslompLabels } from '../../../utils/rompslompLabels';
import {
    UNCATEGORIZED, categoryKeyOf, categoryLabelOf, supplierKeyOf, sortExpenses, summarizeByCategory, amountCentsIncl,
} from './expenseListUtils';

// Toggleable Expenses-table columns for the per-user "Columns" view option -
// keys must match backend EXPENSE_TOGGLEABLE_COLUMNS (routes/expenses.py).
// Checkbox/actions columns are never toggleable, so they're not listed here.
const EXPENSE_COLUMNS = [
    { key: 'date', labelKey: 'expenses.columns.date' },
    { key: 'item', labelKey: 'expenses.columns.item' },
    { key: 'supplier', labelKey: 'expenses.columns.supplier' },
    { key: 'category', labelKey: 'expenses.columns.category' },
    { key: 'price_incl_btw', labelKey: 'expenses.columns.priceInclBtw' },
    { key: 'btw_rate', labelKey: 'expenses.columns.btwRate' },
    { key: 'price_excl_btw', labelKey: 'expenses.columns.priceExclBtw' },
    { key: 'btw_amount', labelKey: 'expenses.columns.btwAmount' },
    { key: 'paid', labelKey: 'expenses.columns.paid' },
    { key: 'remaining', labelKey: 'expenses.columns.remaining' },
    { key: 'status', labelKey: 'expenses.columns.status' },
    { key: 'description', labelKey: 'expenses.columns.description' },
    { key: 'attachment', labelKey: 'expenses.columns.attachment' },
];
const EXPENSE_NUMERIC_TOTAL_COLUMNS = new Set(['price_incl_btw', 'price_excl_btw', 'btw_amount', 'paid', 'remaining']);
const DEFAULT_COLUMN_PREFS = EXPENSE_COLUMNS.reduce((acc, c) => { acc[c.key] = true; return acc; }, {});
const BTW_RATE_OPTIONS = ['0', '9', '21'];
// Minimum widths (px) of the table columns; text columns wrap over several lines, numeric/date ones never break.
const COLUMN_MIN_WIDTHS = {
    date: 104, item: 130, supplier: 130, category: 124, price_incl_btw: 96, btw_rate: 76, price_excl_btw: 96,
    btw_amount: 92, paid: 90, remaining: 96, status: 120, description: 140, attachment: 112,
};
const NARROW_QUERY = '(max-width: 991.98px)';       // phones and iPad portrait: stacked cards instead of the table

// Live match of a CSS media query (false where matchMedia does not exist, e.g. in tests).
function useMediaQuery(query) {
    const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
    const [matches, setMatches] = useState(get);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(query);
        const onChange = () => setMatches(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
    }, [query]);
    return matches;
}

// Parses a stored BTW rate: 21, "21", "21%", "21.00" or a 0.21 fraction -> 21.
// Returns null for missing/unparseable values.
const parseBtwRate = (value) => {
    if (value == null || value === '') return null;
    const n = parseFloat(String(value).replace('%', '').replace(',', '.'));
    if (Number.isNaN(n)) return null;
    return n > 0 && n < 1 ? Math.round(n * 100) : n;
};

// Unique BTW rates of an expense, highest first. Derived from the line items
// (falling back to btw_breakdown / the single expense-level rate) because
// btw_rate is null whenever line items have mixed rates.
export const getExpenseBtwRates = (expense) => {
    let rates = [];
    for (const list of [expense.line_items, expense.btw_breakdown]) {
        rates = (list || []).map((x) => parseBtwRate(x.btw_rate ?? x.rate)).filter((r) => r != null);
        if (rates.length > 0) break;
    }
    if (rates.length === 0) {
        const single = parseBtwRate(expense.btw_rate);
        if (single != null) rates = [single];
    }
    return [...new Set(rates)].sort((a, b) => b - a);
};

function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Client-side mirror of the backend's _compute_btw_breakdown() (routes/expenses.py) -
// used only for live preview + form validation. The stored values always come from
// the server's own computation, never from this.
function computeBtwPreview(priceEntered, includesBtw, ratePercent) {
    const price = parseFloat(priceEntered);
    if (isNaN(price) || price <= 0) return { incl: 0, excl: 0, amount: 0 };
    const rate = parseFloat(ratePercent);
    const fraction = (isNaN(rate) ? 0 : rate) / 100;
    let incl, excl;
    if (includesBtw) {
        incl = round2(price);
        excl = fraction ? round2(incl / (1 + fraction)) : incl;
    } else {
        excl = round2(price);
        incl = round2(excl * (1 + fraction));
    }
    return { incl, excl, amount: round2(incl - excl) };
}

function emptyLineItem() {
    return { description: '', price: '', price_includes_btw: true, btw_rate: '21' };
}

const emptyAccounting = () => ({ supplier_id: null, ledger_account_id: '', vat_treatment: '', invoice_number: '' });

function emptyPaymentRow(userId) {
    return { id: null, user_id: userId ?? '', amount: '', payment_source: 'tabliya_account', manuallyEdited: false, reimbursed: false, reimbursed_date: null };
}

// Auto-split totalPrice evenly across rows that haven't been manually edited,
// leaving manually-edited rows' amounts untouched. The last auto row absorbs
// any rounding remainder so the split always sums exactly to totalPrice.
function splitEvenly(rows, totalPrice) {
    if (rows.length === 1) {
        return [{ ...rows[0], amount: totalPrice.toFixed(2) }];
    }
    const manualSum = rows.reduce((s, r) => s + (r.manuallyEdited ? (parseFloat(r.amount) || 0) : 0), 0);
    const autoIndexes = rows.map((r, i) => (!r.manuallyEdited ? i : -1)).filter((i) => i !== -1);
    if (autoIndexes.length === 0) return rows;
    const remaining = round2(totalPrice - manualSum);
    const share = Math.floor((remaining / autoIndexes.length) * 100) / 100;
    let assigned = 0;
    return rows.map((r, i) => {
        if (!autoIndexes.includes(i)) return r;
        const isLast = i === autoIndexes[autoIndexes.length - 1];
        const amt = isLast ? round2(remaining - assigned) : share;
        assigned += amt;
        return { ...r, amount: amt.toFixed(2) };
    });
}

function toLocalDateKey(date) {
    if (!date || !(date instanceof Date)) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function parseLocalDateKey(dateStr) {
    if (!dateStr) return null;
    const s = String(dateStr).trim().slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
        const y = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        const day = parseInt(m[3], 10);
        const d = new Date(y, month, day);
        if (d.getFullYear() === y && d.getMonth() === month && d.getDate() === day) {
            return toLocalDateKey(d);
        }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return toLocalDateKey(d);
    return null;
}

function getWeekMonday(d) {
    const x = new Date(d);
    const day = x.getDay();
    const daysSinceMonday = (day + 6) % 7;
    x.setDate(x.getDate() - daysSinceMonday);
    x.setHours(0, 0, 0, 0);
    return x;
}

function filterExpensesByRange(expenses, range, weekOffset, monthOffset, yearOffset, customStart, customEnd) {
    const now = new Date();

    if (range === 'Custom' && customStart && customEnd) {
        const sKey = toLocalDateKey(customStart);
        const eKey = toLocalDateKey(customEnd);
        if (!sKey || !eKey) return expenses;
        const [from, to] = sKey <= eKey ? [sKey, eKey] : [eKey, sKey];
        return expenses.filter((e) => {
            const key = parseLocalDateKey(e.purchase_date);
            return key && key >= from && key <= to;
        });
    }

    if (range === 'All') return expenses;

    if (range === 'Week') {
        const ref = new Date(now);
        ref.setDate(ref.getDate() - weekOffset * 7);
        const start = getWeekMonday(ref);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        const sKey = toLocalDateKey(start);
        const eKey = toLocalDateKey(end);
        return expenses.filter((e) => {
            const key = parseLocalDateKey(e.purchase_date);
            return key && key >= sKey && key <= eKey;
        });
    }

    if (range === 'Month') {
        let y = now.getFullYear();
        let m = now.getMonth() + monthOffset;
        while (m > 11) { m -= 12; y += 1; }
        while (m < 0) { m += 12; y -= 1; }
        const monthPrefix = `${y}-${String(m + 1).padStart(2, '0')}`;
        return expenses.filter((e) => {
            const key = parseLocalDateKey(e.purchase_date);
            return key && key.slice(0, 7) === monthPrefix;
        });
    }

    if (range === 'Year') {
        const y = now.getFullYear() - yearOffset;
        return expenses.filter((e) => {
            const key = parseLocalDateKey(e.purchase_date);
            return key && key.slice(0, 4) === String(y);
        });
    }

    return expenses;
}

function sumExpenseRows(rows) {
    return rows.reduce(
        (acc, e) => {
            acc.total += e.total_price || 0;
            acc.price_incl_btw += (e.price_incl_btw ?? e.total_price) || 0;
            acc.price_excl_btw += e.price_excl_btw || 0;
            acc.btw_amount += e.btw_amount || 0;
            acc.paid += e.amount_paid || 0;
            acc.remaining += e.remaining || 0;
            return acc;
        },
        { total: 0, price_incl_btw: 0, price_excl_btw: 0, btw_amount: 0, paid: 0, remaining: 0 }
    );
}

const toCents = (v) => Math.round((Number(v) || 0) * 100);

/**
 * Excl./BTW split of one expense, in integer cents, always reconciling (excl + btw = incl).
 * Prefers stored values; if BTW is missing it is derived from btw_rate; if neither
 * is known (legacy rows) the expense is counted as 0% BTW and flagged as `unknown`.
 */
function expenseBtwCents(e) {
    const incl = toCents(e.price_incl_btw ?? e.total_price);
    const hasBtw = e.btw_amount != null && Number.isFinite(Number(e.btw_amount));
    const hasExcl = e.price_excl_btw != null && Number.isFinite(Number(e.price_excl_btw));
    const rate = e.btw_rate != null && Number.isFinite(Number(e.btw_rate)) ? Number(e.btw_rate) : null;
    let btw;
    if (hasBtw) btw = toCents(e.btw_amount);
    else if (hasExcl) btw = incl - toCents(e.price_excl_btw);
    else if (rate !== null) btw = incl - Math.round(incl / (1 + rate / 100));
    else return { incl, excl: incl, btw: 0, unknown: true };
    return { incl, excl: incl - btw, btw, unknown: false };
}

function sumExpenseBtw(rows) {
    const acc = rows.reduce((a, e) => {
        const r = expenseBtwCents(e);
        a.incl += r.incl;
        a.excl += r.excl;
        a.btw += r.btw;
        if (r.unknown) a.unknownCount += 1;
        return a;
    }, { incl: 0, excl: 0, btw: 0, unknownCount: 0 });
    return { price_excl_btw: acc.excl / 100, btw_amount: acc.btw / 100, unknownCount: acc.unknownCount };
}

function formatDate(d) {
    return formatDateForLanguage(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

const Expenses = () => {
    const { t } = useTranslation();

    const PAYMENT_SOURCES = [
        { value: 'tabliya_account', label: t('expenses.paidBy.tabliyaAccount') },
        { value: 'out_of_pocket', label: t('expenses.paidBy.outOfPocket') },
    ];

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [purchaseDate, setPurchaseDate] = useState(new Date());
    const [items, setItems] = useState('');
    const [description, setDescription] = useState('');
    const [supplier, setSupplier] = useState('');
    // accounting side (supplier record, category, BTW treatment, invoice number): stored in accounting_expense only.
    // accTouched remembers what the person changed, so only that is sent on save (an untouched value is never rewritten).
    const [acc, setAcc] = useState(emptyAccounting());
    const accTouchedRef = useRef({});
    const [accOptions, setAccOptions] = useState({ suppliers: [], cost_accounts: [], treatments: [] });
    const [accountingMap, setAccountingMap] = useState({});
    const [accFilter, setAccFilter] = useState('all');
    const [quickAdd, setQuickAdd] = useState(null);      // null | { name }
    const [lineItems, setLineItems] = useState([emptyLineItem()]);
    const [extractingPdf, setExtractingPdf] = useState(false);
    // Receipt scan (photo -> suggested fields). scanConf holds the confidence label per prefilled field and is
    // cleared field by field as the person edits it; scanInfo holds warnings/duplicates/recognised text.
    const [scanning, setScanning] = useState(false);
    const [scanInfo, setScanInfo] = useState(null);
    const [scanConf, setScanConf] = useState({});
    const [scanError, setScanError] = useState('');
    const [scanPreviewUrl, setScanPreviewUrl] = useState(null);
    const cameraInputRef = useRef(null);
    // What the person has typed or changed (never overwritten by a scan) and the latest form values/scan, read when the
    // (slow) scan answer arrives so that text typed in the meantime is respected.
    const touchedRef = useRef({});
    const latestRef = useRef({});
    const lastScanRef = useRef(null);
    const deepLinkDone = useRef(false);
    const [paymentStatus, setPaymentStatus] = useState('all'); // 'all' | 'partial'
    const [amountPaid, setAmountPaid] = useState('');
    const [attachmentFile, setAttachmentFile] = useState(null);

    const [search, setSearch] = useState('');
    const [range, setRange] = useState('Week');
    const [weekOffset, setWeekOffset] = useState(0);
    const [monthOffset, setMonthOffset] = useState(0);
    const [yearOffset, setYearOffset] = useState(0);
    const [customStartDate, setCustomStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [customEndDate, setCustomEndDate] = useState(new Date());
    const [columnPrefs, setColumnPrefs] = useState(DEFAULT_COLUMN_PREFS);
    const labels = useRompslompLabels();
    const narrow = useMediaQuery(NARROW_QUERY);
    const [sort, setSort] = useState({ key: null, dir: 'asc' });
    const [categoryFilter, setCategoryFilter] = useState([]);   // category keys ('none' = uncategorized)
    const [supplierFilter, setSupplierFilter] = useState([]);   // supplier names
    const [filtersOpen, setFiltersOpen] = useState(false);      // collapsible filter panel on narrow screens
    const [summaryBasis, setSummaryBasis] = useState('incl');   // 'incl' | 'excl'
    const [summaryOpen, setSummaryOpen] = useState(() => !(typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches));
    const [selectedIds, setSelectedIds] = useState([]);
    const selectionFilterKeyRef = useRef(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [viewingAttachmentId, setViewingAttachmentId] = useState(null);

    const userIsSuperAdmin = isSuperAdmin();
    const [payers, setPayers] = useState([]);
    const [paymentRows, setPaymentRows] = useState([emptyPaymentRow(getCurrentUserId())]);
    const [paymentsError, setPaymentsError] = useState('');
    const [activeView, setActiveView] = useState('expenses'); // 'expenses' | 'outOfPocket'
    const [oopPayments, setOopPayments] = useState([]);
    const [oopBusyId, setOopBusyId] = useState(null);
    const [oopSubTab, setOopSubTab] = useState('all'); // 'all' | payer user_id (string)
    const [selectedOopIds, setSelectedOopIds] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [expRes, payersRes, oopRes, columnPrefsRes, accRes, accOptRes] = await Promise.allSettled([
                getExpenses(),
                getExpensePayers(),
                getOutOfPocketPayments(),
                getExpenseColumnPrefs(),
                getExpensesAccounting(),
                getExpenseAccountingOptions(),
            ]);
            if (accRes.status === 'fulfilled') setAccountingMap(accRes.value.data?.rows || {});
            if (accOptRes.status === 'fulfilled' && accOptRes.value.data) setAccOptions(accOptRes.value.data);
            if (expRes.status === 'fulfilled') {
                setExpenses(expRes.value.data || []);
            } else {
                toast.error(expRes.reason?.response?.data?.error || expRes.reason?.message || t('expenses.errors.load'));
                setExpenses([]);
            }
            if (payersRes.status === 'fulfilled') setPayers(payersRes.value.data || []);
            if (oopRes.status === 'fulfilled') {
                setOopPayments(oopRes.value.data || []);
            } else if (activeView === 'outOfPocket') {
                setActiveView('expenses');
            }
            if (columnPrefsRes.status === 'fulfilled' && columnPrefsRes.value.data?.columns) {
                setColumnPrefs({ ...DEFAULT_COLUMN_PREFS, ...columnPrefsRes.value.data.columns });
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleColumnVisible = async (key) => {
        const nextValue = !columnPrefs[key];
        setColumnPrefs((prev) => ({ ...prev, [key]: nextValue }));
        try {
            await updateExpenseColumnPrefs({ [key]: nextValue });
        } catch (err) {
            // Revert on failure so the UI never claims a preference was saved when it wasn't.
            setColumnPrefs((prev) => ({ ...prev, [key]: !nextValue }));
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.columnPrefsSave'));
        }
    };

    const visibleColumns = useMemo(
        () => EXPENSE_COLUMNS.filter((c) => columnPrefs[c.key] !== false),
        [columnPrefs]
    );

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // /expenses?expense=<id> (the receipt scan's "possible duplicate" link) opens that expense once.
    useEffect(() => {
        if (loading || deepLinkDone.current) return;
        const id = new URLSearchParams(window.location.search).get('expense');
        if (!id) return;
        deepLinkDone.current = true;
        const exp = expenses.find((e) => String(e.id) === id);
        if (exp) openEditModal(exp);
    }, [loading, expenses]); // eslint-disable-line react-hooks/exhaustive-deps

    latestRef.current = { supplier, items, description, lineItems };
    const touch = (key) => { touchedRef.current[key] = true; };

    const resetScan = () => {
        lastScanRef.current = null;
        setScanInfo(null);
        setScanConf({});
        setScanError('');
        setScanPreviewUrl((prev) => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
        });
    };
    useEffect(() => { if (!showAddModal) resetScan(); }, [showAddModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // A prefilled field shows the yellow "please check" state until the person edits it.
    const needsCheck = (key) => !!scanConf[key] && scanConf[key] !== 'checked' && scanConf[key] !== 'high';
    const dismissCheck = (key) => setScanConf((prev) => (prev[key] ? { ...prev, [key]: 'checked' } : prev));
    // line items are highlighted per line (each line carries its own confidence)
    const lineNeedsCheck = (i) => { const c = (scanConf.lineConf || [])[i]; return !!c && c !== 'checked' && c !== 'high'; };
    const anyLineNeedsCheck = (scanConf.lineConf || []).some((c) => c && c !== 'checked' && c !== 'high');
    const dismissLine = (i) => setScanConf((prev) => (prev.lineConf && prev.lineConf[i] ? { ...prev, lineConf: prev.lineConf.map((c, j) => (j === i ? 'checked' : c)) } : prev));
    const worstConfidence = (...labels) => (labels.includes('low') ? 'low' : labels.includes('medium') ? 'medium' : 'high');

    const openAddModal = () => {
        resetScan();
        touchedRef.current = {};
        setEditingId(null);
        setItems('');
        setDescription('');
        setSupplier('');
        setAcc(emptyAccounting());
        accTouchedRef.current = {};
        setLineItems([emptyLineItem()]);
        setPaymentStatus('all');
        setAmountPaid('');
        setAttachmentFile(null);
        setPurchaseDate(new Date());
        setPaymentRows([emptyPaymentRow(getCurrentUserId())]);
        setPaymentsError('');
        setShowAddModal(true);
    };

    const openEditModal = (exp) => {
        resetScan();
        // the saved values count as typed: a scan fills nothing here unless the person asks to replace
        touchedRef.current = { supplier: true, date: true, items: true, description: true, lineItems: true };
        setEditingId(exp.id);
        setItems(exp.items || '');
        setDescription(exp.description || '');
        setSupplier(exp.supplier || '');
        const summary = accountingMap[String(exp.id)];
        accTouchedRef.current = {};
        setAcc({
            supplier_id: summary?.supplier_id ?? null, ledger_account_id: summary?.own?.ledger_account_id ?? '',
            vat_treatment: summary?.own?.vat_treatment ?? '', invoice_number: summary?.invoice_number || '',
        });
        // exp.line_items always has at least one entry - a real saved line item,
        // or (for an expense saved before this feature existed) a single
        // synthesized one built server-side from the legacy price/BTW columns.
        // Either way price_entered is exactly what the user originally typed.
        const items_ = exp.line_items && exp.line_items.length > 0 ? exp.line_items : null;
        setLineItems(
            items_
                ? items_.map((li) => ({
                    description: li.description || '',
                    price: String(li.price_entered ?? ''),
                    price_includes_btw: li.price_includes_btw !== false,
                    btw_rate: li.btw_rate != null ? String(li.btw_rate) : '21',
                }))
                : [emptyLineItem()]
        );
        setPaymentStatus(exp.payment_status === 'paid' ? 'all' : 'partial');
        setAmountPaid(exp.payment_status === 'partial' ? String(exp.amount_paid ?? '') : '');
        setAttachmentFile(null);
        const pd = parseLocalDateKey(exp.purchase_date);
        setPurchaseDate(pd ? new Date(pd.replace(/-/g, '/')) : new Date());
        if (exp.payments && exp.payments.length > 0) {
            // Pre-existing rows are marked manuallyEdited so opening the modal never
            // silently reshuffles an already-saved split.
            setPaymentRows(exp.payments.map((p) => ({
                id: p.id,
                user_id: p.user_id,
                amount: String(p.amount ?? ''),
                payment_source: p.payment_source,
                manuallyEdited: true,
                reimbursed: !!p.reimbursed,
                reimbursed_date: p.reimbursed_date || null,
            })));
        } else {
            // Legacy expense with no payment rows yet - pre-fill a default row but
            // don't persist anything until Update is actually clicked.
            setPaymentRows([emptyPaymentRow(getCurrentUserId())]);
        }
        setPaymentsError('');
        setShowAddModal(true);
    };

    // Per-line-item incl./excl./amount preview, client-side mirror of what the
    // backend will compute and store per line (routes/expenses.py::_apply_line_items).
    const lineItemBreakdowns = useMemo(
        () => lineItems.map((li) => computeBtwPreview(li.price, li.price_includes_btw, li.btw_rate)),
        [lineItems]
    );
    // The true total owed (always incl. BTW) - the sum of every line item's own
    // incl. total, regardless of whether that line was entered incl. or excl.
    const totalPriceNum = useMemo(
        () => round2(lineItemBreakdowns.reduce((s, b) => s + b.incl, 0)),
        [lineItemBreakdowns]
    );
    // Combined BTW breakdown grouped by rate (0%/9%/21%) across all line items -
    // the same grouping the backend returns as btw_breakdown on a saved expense.
    const groupedBtwPreview = useMemo(() => {
        const groups = {};
        const order = [];
        lineItems.forEach((li, i) => {
            const rate = li.btw_rate;
            if (!(rate in groups)) {
                groups[rate] = { rate, excl: 0, amount: 0, incl: 0 };
                order.push(rate);
            }
            const b = lineItemBreakdowns[i];
            groups[rate].excl += b.excl;
            groups[rate].amount += b.amount;
            groups[rate].incl += b.incl;
        });
        return order.map((r) => ({
            rate: r,
            excl: round2(groups[r].excl),
            amount: round2(groups[r].amount),
            incl: round2(groups[r].incl),
        }));
    }, [lineItems, lineItemBreakdowns]);

    const updateLineItem = (index, patch) => {
        touch('lineItems');
        dismissLine(index);
        setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...patch } : li)));
    };
    const addLineItem = () => { touch('lineItems'); setLineItems((prev) => [...prev, emptyLineItem()]); };
    const removeLineItem = (index) => {
        touch('lineItems');
        setLineItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
        setScanConf((prev) => (prev.lineConf ? { ...prev, lineConf: prev.lineConf.filter((_, i) => i !== index) } : prev));
    };

    // Apply what a scan returned. By default only EMPTY fields are filled: whatever the person has typed or changed (or an
    // existing expense already holds) is never overwritten; replace=true is the explicit "Replace with scanned values".
    // -> { skipped: [field keys left alone], conf: confidence labels of the fields that were filled }
    const applyScanFields = (fields, confidence, replace) => {
        const cur = latestRef.current;
        const touched = touchedRef.current;
        const skipped = [];
        const conf = {};
        const fill = (key, isEmpty) => (replace || isEmpty ? true : (skipped.push(key), false));
        if (fields.supplier && fill('supplier', !(cur.supplier || '').trim())) {
            setSupplier(fields.supplier);
            conf.supplier = confidence.supplier;
        }
        if (fields.date && fill('date', !touched.date)) {
            setPurchaseDate(new Date(fields.date.replace(/-/g, '/')));
            conf.date = confidence.date;
            touched.date = false;
        }
        if (fields.purchased_item && fill('items', !(cur.items || '').trim())) {
            setItems(fields.purchased_item);
            conf.items = confidence.purchased_item;
        }
        if (fields.description && fill('description', !(cur.description || '').trim())) {
            setDescription(fields.description);
            conf.description = confidence.description;
        }
        if (fields.line_items?.length > 0) {
            const lis = cur.lineItems || [];
            const pristine = !touched.lineItems && lis.length === 1 && lis[0].price === '' && !(lis[0].description || '').trim();
            if (fill('lineItems', pristine)) {
                setLineItems(fields.line_items.map((li) => ({
                    description: li.description || '',
                    price: String(li.price ?? ''),
                    price_includes_btw: li.price_includes_btw !== false,
                    btw_rate: li.btw_rate != null ? String(li.btw_rate) : '21',
                })));
                conf.lineConf = fields.line_items.map((li) => worstConfidence(li.confidence || confidence.line_items, confidence.total));
                touched.lineItems = false;
            }
        }
        // a printed "paid" is the form's default for paid expenses ("All paid"); the method goes into the description
        if (fields.payment_status === 'paid' && !touched.paymentStatus) setPaymentStatus('all');
        return { skipped, conf };
    };

    const handleScanResult = (data) => {
        const { fields, confidence } = data;
        lastScanRef.current = { fields, confidence };
        const { skipped, conf } = applyScanFields(fields, confidence, false);
        setScanConf(conf);
        setScanInfo({ warnings: data.warnings || [], duplicates: data.possible_duplicate || [], textLines: data.text_lines || [], skipped });
        toast.success(t('expenses.scan.success'));
    };

    const replaceWithScan = () => {
        if (!lastScanRef.current) return;
        const { skipped, conf } = applyScanFields(lastScanRef.current.fields, lastScanRef.current.confidence, true);
        setScanConf((prev) => ({ ...prev, ...conf }));
        setScanInfo((prev) => (prev ? { ...prev, skipped } : prev));
    };

    // Photo -> suggested fields. Read-only on the server; everything it returns is only a suggestion that the
    // person reviews before saving. On any failure the form simply stays as it is for manual entry. The browser never
    // judges the file type: the server detects it from the bytes and answers with a clear message if it cannot read it.
    const scanReceipt = async (f) => {
        setScanning(true);
        setScanError('');
        if (isNativelyDisplayable(f)) setScanPreviewUrl(URL.createObjectURL(f)); // other formats: the server sends a JPEG preview
        try {
            const res = await extractExpenseReceipt(await prepareImageForScan(f));
            const data = res.data;
            if (data.preview) {
                setScanPreviewUrl((prev) => {
                    if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
                    return data.preview;
                });
            }
            if (data.status === 'no_text') {
                setScanError(t('expenses.scan.noText'));
                return;
            }
            handleScanResult(data);
        } catch (err) {
            const reason = err.response?.data?.reason;
            const message = reason
                ? t(`expenses.scan.errors.${reason}`)
                : err.response?.status === 429 ? t('expenses.scan.errors.rateLimited') : t('expenses.scan.errors.failed');
            setScanError(message);
            toast.error(message);
        } finally {
            setScanning(false);
        }
    };

    // PDF invoices: same result handling as photos (all fields, empty fields only).
    const scanPdf = async (f) => {
        setExtractingPdf(true);
        try {
            const res = await extractExpensePdfLineItems(await readAsDataUrl(f));
            const result = res.data;
            if (result.status === 'no_text') {
                toast.info(t('expenses.pdfExtract.noText'));
            } else if (result.fields) {
                handleScanResult(result);
            } else if (result.status === 'ok' && result.line_items?.length > 0) {
                // older server answer: BTW line items only
                setLineItems(result.line_items.map((li) => ({
                    description: li.description || '',
                    price: String(li.price ?? ''),
                    price_includes_btw: li.price_includes_btw !== false,
                    btw_rate: li.btw_rate != null ? String(li.btw_rate) : '21',
                })));
                toast.success(t('expenses.pdfExtract.success'));
            } else {
                toast.info(t('expenses.pdfExtract.notFound'));
            }
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.pdfExtract.failed'));
        } finally {
            setExtractingPdf(false);
        }
    };

    const handleAttachmentChange = async (e) => {
        const f = e.target.files?.[0] || null;
        if (f && f.size > 10 * 1024 * 1024) {
            toast.error(t('expenses.fileTooLarge'));
            e.target.value = '';
            return;
        }
        setAttachmentFile(f);
        resetScan();
        if (!f) return;
        if (await isPdfFile(f)) {
            scanPdf(f);
        } else {
            scanReceipt(f);
        }
    };

    const effectivePaymentRows = useMemo(
        () => splitEvenly(paymentRows, totalPriceNum),
        [paymentRows, totalPriceNum]
    );

    const updatePaymentRow = (index, patch) => {
        setPaymentRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const addPaymentRow = () => {
        // Non-super-admins can only ever attribute payments to themselves (e.g. split
        // part company-card / part out-of-pocket), so every new row defaults to self.
        if (!isSuperAdmin()) {
            setPaymentRows((prev) => [...prev, emptyPaymentRow(getCurrentUserId())]);
            return;
        }
        const usedIds = new Set(paymentRows.map((r) => String(r.user_id)));
        const nextPayer = payers.find((p) => !usedIds.has(String(p.id)));
        setPaymentRows((prev) => [...prev, emptyPaymentRow(nextPayer ? nextPayer.id : (payers[0]?.id ?? ''))]);
    };

    const removePaymentRow = (index) => {
        setPaymentRows((prev) => prev.filter((_, i) => i !== index));
    };

    const changeAcc = (field, value, pickedSupplier) => {
        accTouchedRef.current[field] = true;
        setAcc((prev) => ({ ...prev, [field]: value }));
        if (field === 'supplier_id' && pickedSupplier) {
            touch('supplier');
            dismissCheck('supplier');
            setSupplier(pickedSupplier.name);        // the text on the expense follows the chosen supplier (an explicit choice)
        }
    };

    // Only what the person touched is sent; a NEW expense always sends its supplier.
    const buildAccountingPayload = () => {
        const out = {};
        const touched = accTouchedRef.current;
        if (!editingId || touched.supplier_id) out.supplier_id = acc.supplier_id;
        ['ledger_account_id', 'vat_treatment', 'invoice_number'].forEach((k) => {
            if (touched[k]) out[k] = acc[k] === '' ? null : acc[k];
        });
        return out;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const pdKey = toLocalDateKey(purchaseDate);
        if (!pdKey) {
            toast.error(t('expenses.errors.purchaseDateRequired'));
            return;
        }
        if (!items.trim()) {
            toast.error(t('expenses.errors.purchaseItemRequired'));
            return;
        }
        if (!supplier.trim()) {
            toast.error(t('expenses.errors.supplierRequired'));
            return;
        }
        if (!editingId && !acc.supplier_id) {
            toast.error(t('expenses.errors.supplierPickRequired'));
            return;
        }
        if (lineItems.some((li) => !(parseFloat(li.price) > 0))) {
            toast.error(t('expenses.errors.priceRequired'));
            return;
        }
        // totalPriceNum is always the incl.-BTW total - the sum of every line
        // item's own incl. total - what's actually owed/paid.
        let amountPaidNum = 0;
        if (paymentStatus === 'partial') {
            amountPaidNum = parseFloat(amountPaid || '0');
            if (isNaN(amountPaidNum) || amountPaidNum < 0 || amountPaidNum > totalPriceNum) {
                toast.error(t('expenses.errors.amountPaidRange'));
                return;
            }
        }
        if (effectivePaymentRows.some((r) => !r.user_id)) {
            toast.error(t('expenses.errors.payerRequired'));
            return;
        }
        const paymentsTotal = effectivePaymentRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        if (Math.round(paymentsTotal * 100) !== Math.round(totalPriceNum * 100)) {
            setPaymentsError(t('expenses.errors.paymentsSumMismatch', { total: formatEuroForLanguage(paymentsTotal), price: formatEuroForLanguage(totalPriceNum) }));
            return;
        }
        setPaymentsError('');

        setSubmitting(true);
        try {
            let attachment_base64 = null;
            if (attachmentFile) {
                attachment_base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(attachmentFile);
                });
            }

            const payload = {
                purchase_date: pdKey,
                items: items.trim(),
                description: description.trim() || null,
                supplier: supplier.trim(),
                line_items: lineItems.map((li) => ({
                    description: li.description.trim(),
                    price: parseFloat(li.price) || 0,
                    price_includes_btw: li.price_includes_btw,
                    btw_rate: parseFloat(li.btw_rate) || 0,
                })),
                payment_status: paymentStatus,
                amount_paid: paymentStatus === 'partial' ? amountPaidNum : undefined,
                ...(Object.keys(buildAccountingPayload()).length > 0 ? { accounting: buildAccountingPayload() } : {}),
                attachment_base64,
                payments: effectivePaymentRows.map((r) => ({
                    ...(r.id ? { id: r.id } : {}),
                    user_id: r.user_id,
                    amount: parseFloat(r.amount) || 0,
                    payment_source: r.payment_source,
                    ...(r.payment_source === 'out_of_pocket' ? { reimbursed: !!r.reimbursed } : {}),
                })),
            };

            let saved;
            if (editingId) {
                saved = await updateExpense(editingId, payload);
                toast.success(t('expenses.success.updated'));
            } else {
                saved = await createExpense(payload);
                toast.success(t('expenses.success.added'));
            }
            // the receipt was saved, but converting it to JPEG failed: the original file was kept (it opens as a download)
            if (saved?.data?.attachment_warning === 'not_converted') toast.warning(t('expenses.scan.attachmentNotConverted'));
            setItems('');
            setDescription('');
            setSupplier('');
            setAcc(emptyAccounting());
            accTouchedRef.current = {};
            setLineItems([emptyLineItem()]);
            setPaymentStatus('all');
            setAmountPaid('');
            setAttachmentFile(null);
            setPurchaseDate(new Date());
            setPaymentRows([emptyPaymentRow(getCurrentUserId())]);
            setPaymentsError('');
            setShowAddModal(false);
            setEditingId(null);
            setRange('All');
            await load();
        } catch (err) {
            const reason = err.response?.data?.reason;
            toast.error(reason && reason.startsWith('attachment_')
                ? t(`expenses.scan.errors.${reason}`)
                : err.response?.data?.error || err.message || t('expenses.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    const filteredExpenses = useMemo(() => {
        let list = filterExpensesByRange(
            expenses, range, weekOffset, monthOffset, yearOffset, customStartDate, customEndDate
        );

        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter((e) => {
                return (
                    String(e.items || '').toLowerCase().includes(q) ||
                    String(e.supplier || '').toLowerCase().includes(q)
                );
            });
        }
        if (accFilter !== 'all') list = list.filter((e) => matchesAccountingFilter(accFilter, accountingMap[String(e.id)]));
        if (categoryFilter.length > 0) list = list.filter((e) => categoryFilter.includes(categoryKeyOf(e, accountingMap)));
        if (supplierFilter.length > 0) list = list.filter((e) => supplierFilter.includes(supplierKeyOf(e)));
        return list;
    }, [expenses, range, weekOffset, monthOffset, yearOffset, customStartDate, customEndDate, search, accFilter, accountingMap, categoryFilter, supplierFilter]);

    // Category of an expense = the Rompslomp cost category (own or the supplier's default), the same one the sender would use.
    const accountsById = useMemo(() => {
        const map = {};
        (accOptions.cost_accounts || []).forEach((a) => { map[String(a.id)] = a; });
        return map;
    }, [accOptions]);
    const uncategorizedLabel = t('expenses.filters.uncategorized');
    const categoryLabelOfKey = (key) => categoryLabelOf(key, accountsById, labels.account, uncategorizedLabel);
    const categoryLabelOfExpense = (e) => categoryLabelOfKey(categoryKeyOf(e, accountingMap));
    // all categories / suppliers that exist (not only in the current period) so a filter can be picked before it is applied
    const categoryOptions = useMemo(() => {
        const keys = new Set(expenses.map((e) => categoryKeyOf(e, accountingMap)));
        categoryFilter.forEach((k) => keys.add(k));
        return [...keys].map((value) => ({ value, label: categoryLabelOfKey(value) }))
            .sort((a, b) => (a.value === UNCATEGORIZED) - (b.value === UNCATEGORIZED) || a.label.localeCompare(b.label));
    }, [expenses, accountingMap, accountsById, categoryFilter, uncategorizedLabel, labels.language]); // eslint-disable-line react-hooks/exhaustive-deps
    const supplierOptions = useMemo(() => {
        const names = new Set(expenses.map(supplierKeyOf).filter(Boolean));
        supplierFilter.forEach((n) => names.add(n));
        return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((n) => ({ value: n, label: n }));
    }, [expenses, supplierFilter]);
    // stable colour per category (independent of which expenses are visible)
    const colorOfCategory = useMemo(() => {
        const keys = [...new Set(expenses.map((e) => categoryKeyOf(e, accountingMap)))].filter((k) => k !== UNCATEGORIZED).sort((a, b) => Number(a) - Number(b));
        return (key) => SLICE_COLORS[Math.max(0, keys.indexOf(key)) % SLICE_COLORS.length];
    }, [expenses, accountingMap]);

    const sortedExpenses = useMemo(
        () => sortExpenses(filteredExpenses, sort.key, sort.dir, { categoryLabel: categoryLabelOfExpense, btwRates: getExpenseBtwRates }),
        [filteredExpenses, sort, accountingMap, accountsById, labels.language] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const toggleSort = (key) => setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

    const summary = useMemo(
        () => summarizeByCategory(filteredExpenses, {
            accountingMap,
            labelOfKey: categoryLabelOfKey,
            amountCents: summaryBasis === 'excl' ? (e) => expenseBtwCents(e).excl : amountCentsIncl,
            otherLabel: t('expenses.summary.other'),
        }),
        [filteredExpenses, accountingMap, accountsById, summaryBasis, labels.language, uncategorizedLabel] // eslint-disable-line react-hooks/exhaustive-deps
    );
    // slice / legend click: add the category (or all categories inside "Other") to the filter, or remove them again
    const toggleCategoryKeys = (keys) => setCategoryFilter((prev) => (keys.every((k) => prev.includes(k)) ? prev.filter((k) => !keys.includes(k)) : [...new Set([...prev, ...keys])]));

    const activeChips = [
        ...categoryFilter.map((k) => ({ id: `c-${k}`, text: `${t('expenses.columns.category')}: ${categoryLabelOfKey(k)}`, remove: () => setCategoryFilter((p) => p.filter((x) => x !== k)) })),
        ...supplierFilter.map((n) => ({ id: `s-${n}`, text: `${t('expenses.columns.supplier')}: ${n}`, remove: () => setSupplierFilter((p) => p.filter((x) => x !== n)) })),
    ];
    const clearFilters = () => { setCategoryFilter([]); setSupplierFilter([]); setSearch(''); setAccFilter('all'); };
    const hasActiveFilters = activeChips.length > 0 || search.trim() !== '' || accFilter !== 'all';

    useEffect(() => {
        const filterKey = JSON.stringify({
            activeView,
            range,
            weekOffset,
            monthOffset,
            yearOffset,
            customStart: toLocalDateKey(customStartDate),
            customEnd: toLocalDateKey(customEndDate),
            search,
            categoryFilter,
            supplierFilter,
        });

        if (selectionFilterKeyRef.current === null) {
            selectionFilterKeyRef.current = filterKey;
            return;
        }

        if (selectionFilterKeyRef.current !== filterKey) {
            selectionFilterKeyRef.current = filterKey;
            setSelectedIds([]);
        }
    }, [activeView, range, weekOffset, monthOffset, yearOffset, customStartDate, customEndDate, search, categoryFilter, supplierFilter]);

    const allSelected = filteredExpenses.length > 0 && filteredExpenses.every((e) => selectedIds.includes(e.id));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredExpenses.map((e) => e.id));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('expenses.deleteConfirm'))) return;
        try {
            await deleteExpense(id);
            toast.success(t('expenses.success.deleted'));
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.deleteFailed'));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            toast.info(t('expenses.selectAtLeastOneToDelete'));
            return;
        }
        if (!window.confirm(t('expenses.deleteSelectedConfirm', { count: selectedIds.length }))) return;
        try {
            await bulkDeleteExpenses(selectedIds);
            toast.success(t('expenses.success.selectedDeleted'));
            setSelectedIds([]);
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.deleteSelectedFailed'));
        }
    };

    const handleToggleReimbursed = async (payment) => {
        setOopBusyId(payment.id);
        try {
            await setPaymentReimbursed(payment.id, !payment.reimbursed);
            toast.success(t(payment.reimbursed ? 'expenses.success.markedUnpaid' : 'expenses.success.markedReimbursed'));
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.reimburseFailed'));
        } finally {
            setOopBusyId(null);
        }
    };

    const oopSummary = useMemo(() => {
        const unreimbursed = oopPayments.filter((p) => !p.reimbursed);
        const totalOutstanding = unreimbursed.reduce((s, p) => s + (p.amount || 0), 0);
        const byPayer = {};
        unreimbursed.forEach((p) => {
            byPayer[p.payer_name] = (byPayer[p.payer_name] || 0) + (p.amount || 0);
        });
        return { totalOutstanding, byPayer };
    }, [oopPayments]);

    // Per-payer groups for the Out of Pocket sub-tabs, ordered by outstanding amount (highest first).
    const oopGroups = useMemo(() => {
        const byUser = {};
        oopPayments.forEach((p) => {
            const key = String(p.user_id);
            if (!byUser[key]) {
                byUser[key] = { user_id: p.user_id, payer_name: p.payer_name, payments: [], totalOutstanding: 0, totalReimbursed: 0 };
            }
            byUser[key].payments.push(p);
            if (p.reimbursed) byUser[key].totalReimbursed += p.amount || 0;
            else byUser[key].totalOutstanding += p.amount || 0;
        });
        return Object.values(byUser).sort((a, b) => b.totalOutstanding - a.totalOutstanding);
    }, [oopPayments]);

    const activeOopGroup = oopSubTab === 'all' ? null : oopGroups.find((g) => String(g.user_id) === oopSubTab) || null;

    useEffect(() => {
        // If the payer behind the active sub-tab no longer has any out-of-pocket rows
        // (e.g. their last one got deleted), fall back to "All" instead of a dead tab.
        if (oopSubTab !== 'all' && !oopGroups.some((g) => String(g.user_id) === oopSubTab)) {
            setOopSubTab('all');
        }
    }, [oopGroups, oopSubTab]);

    const toggleSelectOopRow = (id) => {
        setSelectedOopIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const toggleSelectAllOop = (rows) => {
        const ids = rows.map((p) => p.id);
        const allOn = ids.length > 0 && ids.every((id) => selectedOopIds.includes(id));
        setSelectedOopIds(allOn ? [] : ids);
    };

    const handleBulkReimburse = async (ids, reimbursed) => {
        if (!ids.length) {
            toast.error(t('expenses.errors.selectAtLeastOneRow'));
            return;
        }
        setBulkBusy(true);
        try {
            await bulkSetPaymentsReimbursed(ids, reimbursed);
            toast.success(t(reimbursed ? 'expenses.success.bulkReimbursed' : 'expenses.success.bulkUnpaid', { count: ids.length }));
            setSelectedOopIds([]);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.bulkReimburseFailed'));
        } finally {
            setBulkBusy(false);
        }
    };

    const handleSettleAll = async (group) => {
        const ids = group.payments.filter((p) => !p.reimbursed).map((p) => p.id);
        if (!ids.length) {
            toast.info(t('expenses.errors.nothingToSettle'));
            return;
        }
        setBulkBusy(true);
        try {
            await bulkSetPaymentsReimbursed(ids, true);
            toast.success(t('expenses.success.settled', { count: ids.length, payer: group.payer_name }));
            setSelectedOopIds([]);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.bulkReimburseFailed'));
        } finally {
            setBulkBusy(false);
        }
    };

    const handleViewAttachment = async (expenseId, attachmentUrl) => {
        setViewingAttachmentId(expenseId);
        try {
            const res = await getExpenseFileBlob(attachmentUrl);
            const blob = res.data;
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            // Revoke after 30 s — enough time for the new tab to load the file
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
            if (!win) {
                // Popup blocked — fall back to forced download
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = attachmentUrl.split('/').pop();
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('expenses.errors.attachmentLoad'));
        } finally {
            setViewingAttachmentId(null);
        }
    };

    const selectedVisibleExpenses = useMemo(
        () => filteredExpenses.filter((e) => selectedIds.includes(e.id)),
        [filteredExpenses, selectedIds]
    );
    const totalRow = useMemo(() => sumExpenseRows(filteredExpenses), [filteredExpenses]);
    const selectedBtwTotals = useMemo(() => sumExpenseBtw(selectedVisibleExpenses), [selectedVisibleExpenses]);
    const selectedTotalRow = useMemo(() => sumExpenseRows(selectedVisibleExpenses), [selectedVisibleExpenses]);

    const cellPad = { paddingLeft: '0.625rem', paddingRight: '0.625rem', paddingTop: '0.5rem', paddingBottom: '0.5rem', boxSizing: 'border-box' };
    // Auto table layout: every column keeps at least its min-width (so headers wrap on word boundaries and the
    // actions never get squeezed); the .table-responsive wrapper scrolls sideways when the sum is wider than the card.
    const expenseCol1Style = { width: '1%', minWidth: 44, ...cellPad };
    const columnStyle = (key) => ({ minWidth: COLUMN_MIN_WIDTHS[key] || 110, ...cellPad });
    const expenseActionsStyle = { width: '1%', minWidth: 96, ...cellPad };
    const totalColumnCount = visibleColumns.length + 2; // checkbox + actions

    // One render function per toggleable column - keyed the same as EXPENSE_COLUMNS/
    // EXPENSE_NUMERIC_TOTAL_COLUMNS above, so thead/tbody/tfoot below all stay in
    // sync purely by iterating visibleColumns, regardless of which are hidden.
    const expenseCellRenderers = {
        date: (e) => ({ content: formatDate(e.purchase_date) }),
        item: (e) => ({ content: e.items, className: 'expense-wrap-cell' }),
        category: (e) => ({ content: categoryLabelOfExpense(e), className: 'expense-wrap-cell' }),
        supplier: (e) => ({
            content: <>{e.supplier}<MissingLine summary={accountingMap[String(e.id)]} /></>,
            className: 'expense-supplier-cell',
            title: [e.supplier, missingText(t, accountingMap[String(e.id)])].filter(Boolean).join(' - '),
        }),
        price_incl_btw: (e) => ({ content: formatEuroForLanguage((e.price_incl_btw ?? e.total_price) || 0) }),
        btw_rate: (e) => {
            const rates = getExpenseBtwRates(e);
            // The per-rate breakdown stays available on hover for mixed rates.
            const breakdown = e.btw_breakdown || [];
            const title = breakdown.length > 1
                ? breakdown.map((g) => `${g.rate != null ? `${g.rate}%` : '?'}: ${formatEuroForLanguage(g.price_incl_btw)}`).join(' · ')
                : undefined;
            return {
                content: rates.length > 0 ? rates.map((r) => `${r}%`).join(', ') : '-',
                className: 'text-nowrap',
                title,
            };
        },
        price_excl_btw: (e) => ({ content: e.price_excl_btw != null ? formatEuroForLanguage(e.price_excl_btw) : '-' }),
        btw_amount: (e) => ({ content: e.btw_amount != null ? formatEuroForLanguage(e.btw_amount) : '-' }),
        paid: (e) => ({ content: formatEuroForLanguage(e.amount_paid || 0) }),
        remaining: (e) => ({ content: formatEuroForLanguage(e.remaining || 0) }),
        status: (e) => ({
            content: (
                <span className={`badge ${e.payment_status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {e.payment_status === 'paid' ? t('expenses.allPaid') : t('expenses.partiallyPaid')}
                </span>
            ),
        }),
        description: (e) => ({ content: e.description || '-', className: 'expense-wrap-cell' }),
        attachment: (e) => ({
            content: e.attachment_url ? (
                <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleViewAttachment(e.id, e.attachment_url)}
                    disabled={viewingAttachmentId === e.id}
                >
                    {viewingAttachmentId === e.id
                        ? <Spinner animation="border" size="sm" />
                        : <><i className="fa-solid fa-eye me-1" />{t('common.view')}</>}
                </Button>
            ) : '-',
        }),
    };

    const expensesTableId = 'expenses-table-fixed-cols';

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h4 className="mb-0">{t('expenses.title')}</h4>
                            <Button variant="primary" onClick={openAddModal}>
                                {t('expenses.addExpense')}
                            </Button>
                        </Card.Header>
                    </Card>

                    <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setEditingId(null); }} size={scanPreviewUrl ? 'xl' : 'lg'} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{editingId ? t('expenses.editExpenseTitle') : t('expenses.addExpenseTitle')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className={scanPreviewUrl ? 'receipt-split' : undefined}>
                            <Form id="add-expense-form" onSubmit={handleSubmit}>
                                <Row className="g-3 align-items-end">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t('expenses.purchaseDate')}<span className="text-danger">*</span></Form.Label>
                                            <DatePicker
                                                selected={purchaseDate}
                                                onChange={(d) => { touch('date'); dismissCheck('date'); setPurchaseDate(d || new Date()); }}
                                                className={`form-control${needsCheck('date') ? ' receipt-check' : ''}`}
                                                dateFormat="yyyy-MM-dd"
                                            />
                                            {needsCheck('date') && <Form.Text className="receipt-check-hint">{t('expenses.scan.checkField')}</Form.Text>}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t('expenses.purchaseItem')}<span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={items}
                                                onChange={(e) => { touch('items'); dismissCheck('items'); setItems(e.target.value); }}
                                                placeholder={t('expenses.purchaseItemPlaceholder')}
                                                className={needsCheck('items') ? 'receipt-check' : undefined}
                                                required
                                            />
                                            {needsCheck('items') && <Form.Text className="receipt-check-hint">{t('expenses.scan.checkField')}</Form.Text>}
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <ExpenseAccountingFields options={accOptions} acc={acc} onChange={changeAcc}
                                            summary={editingId ? accountingMap[String(editingId)] : null}
                                            supplierText={supplier} onSupplierText={(v) => { touch('supplier'); dismissCheck('supplier'); setSupplier(v); }}
                                            onAddSupplier={(name) => setQuickAdd({ name })} supplierInvalid={needsCheck('supplier')} />
                                        {needsCheck('supplier') && <Form.Text className="receipt-check-hint">{t('expenses.scan.checkField')}</Form.Text>}
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t('expenses.paymentStatus')}<span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={paymentStatus}
                                                onChange={(e) => setPaymentStatus(e.target.value)}
                                            >
                                                <option value="all">{t('expenses.allPaid')}</option>
                                                <option value="partial">{t('expenses.partiallyPaid')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                        {paymentStatus === 'partial' && (
                                            <Form.Group className="mt-2">
                                                <Form.Label>{t('expenses.amountPaid')}</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={amountPaid}
                                                    onChange={(e) => setAmountPaid(e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t('expenses.description')}</Form.Label>
                                            <AutoGrowTextarea
                                                value={description}
                                                onChange={(e) => { touch('description'); dismissCheck('description'); setDescription(e.target.value); }}
                                                placeholder={t('expenses.descriptionPlaceholder')}
                                                className={needsCheck('description') ? 'receipt-check' : undefined}
                                            />
                                            {needsCheck('description') && <Form.Text className="receipt-check-hint">{t('expenses.scan.checkField')}</Form.Text>}
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t('expenses.scan.title')}</Form.Label>
                                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                                <Form.Control
                                                    type="file"
                                                    className="receipt-file-input"
                                                    aria-label={t('expenses.scan.chooseFile')}
                                                    data-testid="receipt-file-input"
                                                    accept={RECEIPT_ACCEPT}
                                                    onChange={handleAttachmentChange}
                                                />
                                                <input
                                                    ref={cameraInputRef}
                                                    type="file"
                                                    className="d-none"
                                                    data-testid="receipt-camera-input"
                                                    accept="image/*"
                                                    capture="environment"
                                                    onChange={handleAttachmentChange}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    type="button"
                                                    className="d-md-none"
                                                    onClick={() => cameraInputRef.current?.click()}
                                                >
                                                    {t('expenses.scan.takePhoto')}
                                                </Button>
                                            </div>
                                            <Form.Text className="text-muted d-block mt-1">{t('expenses.scan.photoHint')}</Form.Text>
                                            <Form.Text className="text-muted d-block">{t('expenses.scan.formatNote')}</Form.Text>
                                            {scanning && (
                                                <div className="text-muted small mt-1" role="status">
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    {t('expenses.scan.reading')}
                                                </div>
                                            )}
                                            {scanError && <div className="text-danger small mt-1" role="alert" data-testid="receipt-scan-error">{scanError}</div>}
                                            {extractingPdf && (
                                                <div className="text-muted small mt-1">
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    {t('expenses.pdfExtract.extracting')}
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    {scanInfo && (
                                        <Col md={12}>
                                            <ReceiptScanResult info={scanInfo} onReplace={replaceWithScan} />
                                        </Col>
                                    )}
                                    <Col md={12}>
                                        <hr />
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Form.Label className="mb-0 fw-semibold">
                                                {t('expenses.lineItems.title')}<span className="text-danger">*</span>
                                                {anyLineNeedsCheck && <span className="receipt-check-hint ms-2 fw-normal">{t('expenses.scan.checkField')}</span>}
                                            </Form.Label>
                                            <Button size="sm" variant="outline-primary" type="button" onClick={addLineItem}>
                                                {t('expenses.lineItems.addLineItem')}
                                            </Button>
                                        </div>
                                        {lineItems.map((li, index) => (
                                            <Row key={index} className={`g-2 align-items-end mb-2${lineNeedsCheck(index) ? ' receipt-check-row' : ''}`}>
                                                <Col md={4}>
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        placeholder={t('expenses.lineItems.descriptionPlaceholder')}
                                                        value={li.description}
                                                        onChange={(e) => updateLineItem(index, { description: e.target.value })}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Control
                                                        size="sm"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        value={li.price}
                                                        onChange={(e) => updateLineItem(index, { price: e.target.value })}
                                                        required
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={li.btw_rate}
                                                        onChange={(e) => updateLineItem(index, { btw_rate: e.target.value })}
                                                    >
                                                        {BTW_RATE_OPTIONS.map((r) => (
                                                            <option key={r} value={r}>{r}%</option>
                                                        ))}
                                                        {!BTW_RATE_OPTIONS.includes(li.btw_rate) && (
                                                            <option value={li.btw_rate}>{li.btw_rate}%</option>
                                                        )}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`line-item-incl-btw-${index}`}
                                                        label={li.price_includes_btw ? t('expenses.btw.priceIsIncl') : t('expenses.btw.priceIsExcl')}
                                                        checked={li.price_includes_btw}
                                                        onChange={(e) => updateLineItem(index, { price_includes_btw: e.target.checked })}
                                                    />
                                                </Col>
                                                <Col md={1} className="d-flex align-items-center">
                                                    {lineItems.length > 1 && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            type="button"
                                                            onClick={() => removeLineItem(index)}
                                                            title={t('expenses.lineItems.removeLineItem')}
                                                        >
                                                            <i className="fa-solid fa-minus" />
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Row>
                                        ))}
                                        {lineItems.some((li) => li.price !== '') && (
                                            <div className="text-muted small mt-2">
                                                {groupedBtwPreview.map((g) => (
                                                    <div key={g.rate}>
                                                        {t('expenses.btw.previewPerRate', {
                                                            rate: g.rate,
                                                            incl: formatEuroForLanguage(g.incl),
                                                            excl: formatEuroForLanguage(g.excl),
                                                            amount: formatEuroForLanguage(g.amount),
                                                        })}
                                                    </div>
                                                ))}
                                                <div className="fw-semibold mt-1">
                                                    {t('expenses.lineItems.total', { total: formatEuroForLanguage(totalPriceNum) })}
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        <hr />
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Form.Label className="mb-0 fw-semibold">{t('expenses.paidBy.title')}</Form.Label>
                                            <Button size="sm" variant="outline-primary" type="button" onClick={addPaymentRow}>
                                                {userIsSuperAdmin ? t('expenses.paidBy.addPayer') : t('expenses.paidBy.addPayment')}
                                            </Button>
                                        </div>
                                        {!userIsSuperAdmin && (
                                            <div className="text-muted small mb-2">
                                                {t('expenses.paidBy.selfOnlyNote')}
                                            </div>
                                        )}
                                        {effectivePaymentRows.map((row, index) => (
                                            <Row key={index} className="g-2 align-items-end mb-2">
                                                <Col md={4}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={row.user_id}
                                                        disabled={!userIsSuperAdmin}
                                                        onChange={(e) => updatePaymentRow(index, { user_id: e.target.value })}
                                                    >
                                                        <option value="">{t('expenses.paidBy.selectPayer')}</option>
                                                        {(userIsSuperAdmin ? payers : payers.filter((p) => String(p.id) === String(getCurrentUserId()))).map((p) => (
                                                            <option key={p.id} value={p.id}>{p.username}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Control
                                                        size="sm"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={row.amount}
                                                        disabled={effectivePaymentRows.length === 1}
                                                        onChange={(e) => updatePaymentRow(index, { amount: e.target.value, manuallyEdited: true })}
                                                    />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={row.payment_source}
                                                        onChange={(e) => updatePaymentRow(index, { payment_source: e.target.value })}
                                                    >
                                                        {PAYMENT_SOURCES.map((s) => (
                                                            <option key={s.value} value={s.value}>{s.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={2} className="d-flex align-items-center gap-2">
                                                    {row.payment_source === 'out_of_pocket' && (
                                                        <Form.Check
                                                            type="checkbox"
                                                            label={t('expenses.paidBy.reimbursed')}
                                                            checked={!!row.reimbursed}
                                                            onChange={(e) => updatePaymentRow(index, { reimbursed: e.target.checked })}
                                                        />
                                                    )}
                                                    {effectivePaymentRows.length > 1 && (
                                                        <Button size="sm" variant="outline-danger" type="button" onClick={() => removePaymentRow(index)} title={t('expenses.paidBy.removePayer')}>
                                                            <i className="fa-solid fa-minus" />
                                                        </Button>
                                                    )}
                                                </Col>
                                                {row.payment_source === 'out_of_pocket' && row.reimbursed && (
                                                    <Col md={12} className="text-muted small">
                                                        {t('expenses.paidBy.reimbursedDateLabel', { date: row.reimbursed_date || t('expenses.paidBy.reimbursedDateAuto') })}
                                                    </Col>
                                                )}
                                            </Row>
                                        ))}
                                        {paymentsError && <div className="text-danger small mt-1">{paymentsError}</div>}
                                    </Col>
                                </Row>
                            </Form>
                            {scanPreviewUrl && <ReceiptPreview url={scanPreviewUrl} />}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setShowAddModal(false); setEditingId(null); }}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" type="submit" form="add-expense-form" disabled={submitting}>
                                {submitting ? <Spinner animation="border" size="sm" /> : editingId ? t('common.update') : t('expenses.addExpense')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <SupplierQuickAdd
                        show={!!quickAdd}
                        initialName={quickAdd?.name || ''}
                        onHide={() => setQuickAdd(null)}
                        onCreated={async (created) => {
                            setQuickAdd(null);
                            try { setAccOptions((await getExpenseAccountingOptions()).data); } catch (_) { /* the new supplier is still selected below */ }
                            changeAcc('supplier_id', created.id, created);
                        }}
                    />
                </div>
            </div>

            {activeView === 'expenses' && !loading && (
                <ExpenseCategorySummary
                    summary={summary}
                    colorOf={colorOfCategory}
                    basis={summaryBasis}
                    onBasisChange={setSummaryBasis}
                    selectedKeys={categoryFilter}
                    onToggleKey={toggleCategoryKeys}
                    open={summaryOpen}
                    onToggleOpen={() => setSummaryOpen((v) => !v)}
                    narrow={narrow}
                />
            )}

            <Card>
                <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <div className="btn-group" role="group">
                            {['Week', 'Month', 'Year', 'All', 'Custom'].map((label) => (
                                <Button
                                    key={label}
                                    size="sm"
                                    variant={activeView === 'expenses' && range === label ? 'primary' : 'light'}
                                    onClick={() => {
                                        setActiveView('expenses');
                                        setRange(label);
                                        if (label !== 'Week') setWeekOffset(0);
                                        if (label !== 'Month') setMonthOffset(0);
                                        if (label !== 'Year') setYearOffset(0);
                                    }}
                                >
                                    {label === 'Week' ? t('expenses.tabs.thisWeek')
                                        : label === 'Month' ? t('expenses.tabs.month')
                                        : label === 'Year' ? t('expenses.tabs.year')
                                        : label === 'All' ? t('common.all')
                                        : t('expenses.tabs.custom')}
                                </Button>
                            ))}
                            {oopPayments.length > 0 && (
                                <Button
                                    size="sm"
                                    variant={activeView === 'outOfPocket' ? 'primary' : 'light'}
                                    onClick={() => setActiveView('outOfPocket')}
                                >
                                    {t('expenses.tabs.outOfPocket')}
                                </Button>
                            )}
                        </div>
                        {activeView === 'expenses' && range === 'Week' && (
                            <div className="ms-2 btn-group" role="group">
                                <Button size="sm" variant="outline-secondary" onClick={() => setWeekOffset((v) => v + 1)}>
                                    {t('expenses.prevWeek')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setWeekOffset((v) => Math.max(0, v - 1))}
                                    disabled={weekOffset === 0}
                                >
                                    {t('expenses.nextWeek')}
                                </Button>
                            </div>
                        )}
                        {activeView === 'expenses' && range === 'Month' && (
                            <div className="ms-2 btn-group" role="group">
                                <Button size="sm" variant="outline-secondary" onClick={() => setMonthOffset((v) => v - 1)}>
                                    {t('expenses.prevMonth')}
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={() => setMonthOffset((v) => v + 1)}>
                                    {t('expenses.nextMonth')}
                                </Button>
                            </div>
                        )}
                        {activeView === 'expenses' && range === 'Year' && (
                            <div className="ms-2 btn-group" role="group">
                                <Button size="sm" variant="outline-secondary" onClick={() => setYearOffset((v) => v + 1)}>
                                    {t('expenses.prevYear')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setYearOffset((v) => Math.max(0, v - 1))}
                                    disabled={yearOffset === 0}
                                >
                                    {t('expenses.nextYear')}
                                </Button>
                            </div>
                        )}
                        {activeView === 'expenses' && range === 'Custom' && (
                            <div className="d-flex align-items-center ms-2 gap-2">
                                <DatePicker
                                    selected={customStartDate}
                                    onChange={(d) => setCustomStartDate(d || customStartDate)}
                                    className="form-control form-control-sm"
                                    dateFormat="yyyy-MM-dd"
                                />
                                <span>{t('expenses.to')}</span>
                                <DatePicker
                                    selected={customEndDate}
                                    onChange={(d) => setCustomEndDate(d || customEndDate)}
                                    className="form-control form-control-sm"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                        )}
                    </div>
                    {activeView === 'expenses' && (
                        <div className="expense-filters d-flex flex-wrap gap-2 align-items-center mt-2 mt-sm-0">
                            {narrow && (
                                <Button size="sm" variant="outline-secondary" onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen} data-testid="expense-filters-toggle">
                                    <i className="fa-solid fa-filter me-1" />{t('expenses.filters.title')}{activeChips.length ? ` (${activeChips.length})` : ''}
                                </Button>
                            )}
                            {(!narrow || filtersOpen) && (
                                <div className="expense-filters-panel d-flex flex-wrap gap-2 align-items-center" data-testid="expense-filters-panel">
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder={t('expenses.searchPlaceholder')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="expense-search"
                                    />
                                    <Form.Select size="sm" aria-label={t('expenses.accounting.filter')} value={accFilter} onChange={(e) => setAccFilter(e.target.value)} data-testid="accounting-filter">
                                        {ACCOUNTING_FILTERS.map((k) => <option key={k} value={k}>{t(`expenses.accounting.filters.${k}`)}</option>)}
                                    </Form.Select>
                                    <ExpenseMultiSelect id="expense-category-filter" label={t('expenses.columns.category')} options={categoryOptions}
                                        selected={categoryFilter} onChange={setCategoryFilter} searchPlaceholder={t('expenses.filters.searchCategories')} />
                                    <ExpenseMultiSelect id="expense-supplier-filter" label={t('expenses.columns.supplier')} options={supplierOptions}
                                        selected={supplierFilter} onChange={setSupplierFilter} searchPlaceholder={t('expenses.filters.searchSuppliers')} />
                                </div>
                            )}
                            <Dropdown autoClose="outside">
                                <Dropdown.Toggle size="sm" variant="outline-secondary" id="expenses-columns-dropdown">
                                    <i className="fa-solid fa-table-columns me-1" />{t('expenses.columnsMenu.title')}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="p-2" style={{ minWidth: 220 }}>
                                    {EXPENSE_COLUMNS.map((col) => (
                                        <Form.Check
                                            key={col.key}
                                            type="checkbox"
                                            id={`expenses-column-toggle-${col.key}`}
                                            className="mb-1"
                                            label={t(col.labelKey)}
                                            checked={columnPrefs[col.key] !== false}
                                            onChange={() => toggleColumnVisible(col.key)}
                                        />
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            <Button size="sm" variant="outline-danger" onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
                                {t('expenses.deleteSelected')}
                            </Button>
                        </div>
                    )}
                </Card.Header>
                {activeView === 'expenses' && hasActiveFilters && (
                    <div className="expense-chips px-3 py-2 border-bottom" data-testid="expense-chips">
                        {activeChips.map((c) => (
                            <span key={c.id} className="expense-chip">
                                {c.text}
                                <button type="button" onClick={c.remove} aria-label={t('expenses.filters.removeChip', { name: c.text })}>
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            </span>
                        ))}
                        <Button size="sm" variant="link" onClick={clearFilters} data-testid="expense-clear-filters">{t('expenses.filters.clear')}</Button>
                    </div>
                )}
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" className="text-primary" />
                        </div>
                    ) : activeView === 'outOfPocket' ? (
                        <div className="table-responsive">
                            <div className="px-3 pt-3 d-flex flex-wrap gap-2 align-items-center border-bottom pb-3 mb-2">
                                <Button
                                    size="sm"
                                    variant={oopSubTab === 'all' ? 'primary' : 'light'}
                                    onClick={() => { setOopSubTab('all'); setSelectedOopIds([]); }}
                                >
                                    {t('expenses.outOfPocket.all')}
                                </Button>
                                {oopGroups.map((g) => (
                                    <Button
                                        key={g.user_id}
                                        size="sm"
                                        variant={oopSubTab === String(g.user_id) ? 'primary' : 'light'}
                                        onClick={() => { setOopSubTab(String(g.user_id)); setSelectedOopIds([]); }}
                                    >
                                        {g.payer_name} <span className="badge bg-secondary ms-1">{formatEuroForLanguage(g.totalOutstanding)}</span>
                                    </Button>
                                ))}
                            </div>

                            {oopSubTab === 'all' ? (
                                <>
                                    <div className="px-3 pb-1">
                                        <div className="fw-semibold">
                                            {t('expenses.outOfPocket.totalOutstanding')}: {formatEuroForLanguage(oopSummary.totalOutstanding)}
                                        </div>
                                        {Object.keys(oopSummary.byPayer).length > 0 && (
                                            <div className="text-muted small">
                                                {Object.entries(oopSummary.byPayer)
                                                    .map(([name, amt]) => t('expenses.outOfPocket.owed', { name, amount: formatEuroForLanguage(amt) }))
                                                    .join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                    <table className="table table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>{t('expenses.columns.date')}</th>
                                                <th>{t('expenses.columns.item')}</th>
                                                <th>{t('expenses.columns.payer')}</th>
                                                <th>{t('expenses.columns.amount')}</th>
                                                <th>{t('expenses.columns.reimbursed')}</th>
                                                <th>{t('expenses.columns.action')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {oopPayments.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-4">{t('expenses.outOfPocket.noPayments')}</td>
                                                </tr>
                                            ) : (
                                                oopPayments.map((p) => (
                                                    <tr key={p.id}>
                                                        <td>{formatDate(p.purchase_date)}</td>
                                                        <td className="text-truncate" title={p.items}>{p.items}</td>
                                                        <td>{p.payer_name}</td>
                                                        <td>{formatEuroForLanguage(p.amount || 0)}</td>
                                                        <td>
                                                            <span className={`badge ${p.reimbursed ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                                {p.reimbursed ? t('expenses.paidBy.reimbursed') : t('expenses.outOfPocket.unreimbursed')}
                                                            </span>
                                                            {p.reimbursed && p.reimbursed_date && (
                                                                <span className="text-muted small ms-1">({formatDate(p.reimbursed_date)})</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                variant={p.reimbursed ? 'outline-secondary' : 'primary'}
                                                                disabled={oopBusyId === p.id}
                                                                onClick={() => handleToggleReimbursed(p)}
                                                            >
                                                                {oopBusyId === p.id
                                                                    ? <Spinner animation="border" size="sm" />
                                                                    : p.reimbursed ? t('expenses.outOfPocket.markUnpaid') : t('expenses.outOfPocket.markReimbursed')}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </>
                            ) : activeOopGroup ? (
                                <>
                                    <div className="px-3 pb-2 d-flex flex-wrap justify-content-between align-items-start gap-2">
                                        <div>
                                            <div className="fw-semibold">
                                                {t('expenses.outOfPocket.totalOutstanding')}: {formatEuroForLanguage(activeOopGroup.totalOutstanding)}
                                            </div>
                                            {activeOopGroup.totalReimbursed > 0 && (
                                                <div className="text-muted small">
                                                    {t('expenses.outOfPocket.reimbursedSoFar', { amount: formatEuroForLanguage(activeOopGroup.totalReimbursed) })}
                                                </div>
                                            )}
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={selectedOopIds.length === 0 || bulkBusy}
                                                onClick={() => handleBulkReimburse(selectedOopIds, true)}
                                            >
                                                {t('expenses.outOfPocket.markSelectedReimbursed')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={selectedOopIds.length === 0 || bulkBusy}
                                                onClick={() => handleBulkReimburse(selectedOopIds, false)}
                                            >
                                                {t('expenses.outOfPocket.markSelectedUnpaid')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                disabled={activeOopGroup.totalOutstanding <= 0 || bulkBusy}
                                                onClick={() => handleSettleAll(activeOopGroup)}
                                            >
                                                {t('expenses.outOfPocket.settleAll')}
                                            </Button>
                                        </div>
                                    </div>
                                    <table className="table table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '3%' }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={activeOopGroup.payments.length > 0 && activeOopGroup.payments.every((p) => selectedOopIds.includes(p.id))}
                                                        onChange={() => toggleSelectAllOop(activeOopGroup.payments)}
                                                        title={t('expenses.outOfPocket.selectAll')}
                                                    />
                                                </th>
                                                <th>{t('expenses.columns.date')}</th>
                                                <th>{t('expenses.columns.item')}</th>
                                                <th>{t('expenses.columns.amount')}</th>
                                                <th>{t('expenses.columns.reimbursed')}</th>
                                                <th>{t('expenses.columns.action')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeOopGroup.payments.map((p) => (
                                                <tr key={p.id}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedOopIds.includes(p.id)}
                                                            onChange={() => toggleSelectOopRow(p.id)}
                                                        />
                                                    </td>
                                                    <td>{formatDate(p.purchase_date)}</td>
                                                    <td className="text-truncate" title={p.items}>{p.items}</td>
                                                    <td>{formatEuroForLanguage(p.amount || 0)}</td>
                                                    <td>
                                                        <span className={`badge ${p.reimbursed ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                            {p.reimbursed ? t('expenses.paidBy.reimbursed') : t('expenses.outOfPocket.unreimbursed')}
                                                        </span>
                                                        {p.reimbursed && p.reimbursed_date && (
                                                            <span className="text-muted small ms-1">({formatDate(p.reimbursed_date)})</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant={p.reimbursed ? 'outline-secondary' : 'primary'}
                                                            disabled={oopBusyId === p.id}
                                                            onClick={() => handleToggleReimbursed(p)}
                                                        >
                                                            {oopBusyId === p.id
                                                                ? <Spinner animation="border" size="sm" />
                                                                : p.reimbursed ? t('expenses.outOfPocket.markUnpaid') : t('expenses.outOfPocket.markReimbursed')}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            ) : null}
                        </div>
                    ) : (
                        <div className="table-responsive expenses-table-wrapper">
                            {selectedVisibleExpenses.length > 0 && (
                                <div
                                    className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-bottom"
                                    style={{ background: '#f4f0ff' }}
                                    data-testid="expenses-selected-total"
                                >
                                    <span className="fw-semibold text-primary">
                                        {t('expenses.selectedTotal')}
                                    </span>
                                    <span className="badge bg-primary">
                                        {t('expenses.selectedCount', { count: selectedVisibleExpenses.length })}
                                    </span>
                                    <span>
                                        <strong>{t('expenses.columns.priceExclBtw')}:</strong>{' '}
                                        {formatEuroForLanguage(selectedBtwTotals.price_excl_btw)}
                                    </span>
                                    <span>
                                        <strong>{t('expenses.columns.btwAmount')}:</strong>{' '}
                                        {formatEuroForLanguage(selectedBtwTotals.btw_amount)}
                                    </span>
                                    <span>
                                        <strong>{t('expenses.columns.priceInclBtw')}:</strong>{' '}
                                        {formatEuroForLanguage(selectedTotalRow.price_incl_btw)}
                                    </span>
                                    {selectedBtwTotals.unknownCount > 0 && (
                                        <small className="text-muted w-100">
                                            {t('expenses.selectedBtwUnknown', { count: selectedBtwTotals.unknownCount })}
                                        </small>
                                    )}
                                </div>
                            )}
                            {narrow ? (
                                <div className="expense-card-list" data-testid="expense-cards">
                                    {sortedExpenses.length > 0 && (
                                        <Form.Check type="checkbox" id="expense-cards-select-all" className="px-2" label={t('expenses.outOfPocket.selectAll')} checked={allSelected} onChange={toggleSelectAll} />
                                    )}
                                    {sortedExpenses.length === 0 && <div className="text-center text-muted py-4">{t('expenses.noExpensesInRange')}</div>}
                                    {sortedExpenses.map((e) => {
                                        const rates = getExpenseBtwRates(e);
                                        return (
                                            <article key={e.id} className="expense-card" data-testid="expense-card">
                                                <div className="expense-card-head">
                                                    <div>
                                                        <div className="text-muted small">{formatDate(e.purchase_date)}</div>
                                                        <div className="expense-card-item">{e.items}</div>
                                                    </div>
                                                    <label className="expense-card-check">
                                                        <Form.Check type="checkbox" aria-label={t('expenses.outOfPocket.selectAll')} checked={selectedIds.includes(e.id)} onChange={() => toggleSelectOne(e.id)} />
                                                    </label>
                                                </div>
                                                <dl>
                                                    <dt>{t('expenses.columns.supplier')}</dt>
                                                    <dd>{e.supplier || '-'}<MissingLine summary={accountingMap[String(e.id)]} /></dd>
                                                    <dt>{t('expenses.columns.category')}</dt>
                                                    <dd>{categoryLabelOfExpense(e)}</dd>
                                                    <dt>{t('expenses.columns.priceInclBtw')}</dt>
                                                    <dd>{formatEuroForLanguage((e.price_incl_btw ?? e.total_price) || 0)}</dd>
                                                    <dt>{t('expenses.columns.priceExclBtw')}</dt>
                                                    <dd>{e.price_excl_btw != null ? formatEuroForLanguage(e.price_excl_btw) : '-'}</dd>
                                                    <dt>{t('expenses.columns.btwRate')}</dt>
                                                    <dd>{rates.length > 0 ? rates.map((r) => `${r}%`).join(', ') : '-'}</dd>
                                                    <dt>{t('expenses.columns.paid')}</dt>
                                                    <dd>{formatEuroForLanguage(e.amount_paid || 0)}</dd>
                                                    <dt>{t('expenses.columns.status')}</dt>
                                                    <dd>{expenseCellRenderers.status(e).content}</dd>
                                                </dl>
                                                {e.description && <div className="small text-muted mb-2">{e.description}</div>}
                                                <div className="expense-card-actions">
                                                    {e.attachment_url && (
                                                        <Button variant="outline-primary" onClick={() => handleViewAttachment(e.id, e.attachment_url)} disabled={viewingAttachmentId === e.id}>
                                                            <i className="fa-solid fa-eye me-1" />{t('common.view')}
                                                        </Button>
                                                    )}
                                                    <Button variant="primary" onClick={() => openEditModal(e)}><i className="fa-solid fa-pen me-1" />{t('common.edit')}</Button>
                                                    <Button variant="outline-danger" onClick={() => handleDelete(e.id)}><i className="fa-solid fa-trash me-1" />{t('common.delete')}</Button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                    {sortedExpenses.length > 0 && (
                                        <div className="expense-card fw-semibold" data-testid="expense-cards-total">
                                            <div>{t('expenses.totals')}</div>
                                            <dl>
                                                <dt>{t('expenses.columns.priceInclBtw')}</dt><dd>{formatEuroForLanguage(totalRow.price_incl_btw)}</dd>
                                                <dt>{t('expenses.columns.priceExclBtw')}</dt><dd>{formatEuroForLanguage(totalRow.price_excl_btw)}</dd>
                                                <dt>{t('expenses.columns.btwAmount')}</dt><dd>{formatEuroForLanguage(totalRow.btw_amount)}</dd>
                                                <dt>{t('expenses.columns.paid')}</dt><dd>{formatEuroForLanguage(totalRow.paid)}</dd>
                                                <dt>{t('expenses.columns.remaining')}</dt><dd>{formatEuroForLanguage(totalRow.remaining)}</dd>
                                            </dl>
                                        </div>
                                    )}
                                </div>
                            ) : (
                            <table
                                id={expensesTableId}
                                className="table table-bordered mb-0"
                            >
                                <thead>
                                    <tr>
                                        <th style={expenseCol1Style}><Form.Check type="checkbox" checked={allSelected} onChange={toggleSelectAll} /></th>
                                        {visibleColumns.map((col) => {
                                            const active = sort.key === col.key;
                                            return (
                                                <th key={col.key} style={columnStyle(col.key)} className="expense-sortable"
                                                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                                    <button type="button" className="expense-sort-btn" onClick={() => toggleSort(col.key)} data-testid={`sort-${col.key}`}>
                                                        {t(col.labelKey)}
                                                        <i className={`fa-solid ${active ? (sort.dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down') : 'fa-sort'} expense-sort-icon`} aria-hidden="true" />
                                                    </button>
                                                </th>
                                            );
                                        })}
                                        <th style={expenseActionsStyle} className="expense-actions-cell">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedExpenses.length === 0 ? (
                                        <tr>
                                            <td colSpan={totalColumnCount} className="text-center text-muted py-4">{t('expenses.noExpensesInRange')}</td>
                                        </tr>
                                    ) : (
                                        sortedExpenses.map((e) => (
                                            <tr key={e.id}>
                                                <td style={expenseCol1Style}><Form.Check type="checkbox" checked={selectedIds.includes(e.id)} onChange={() => toggleSelectOne(e.id)} /></td>
                                                {visibleColumns.map((col) => {
                                                    const cell = expenseCellRenderers[col.key](e);
                                                    return (
                                                        <td key={col.key} style={columnStyle(col.key)} className={cell.className} title={cell.title}>
                                                            {cell.content}
                                                        </td>
                                                    );
                                                })}
                                                <td style={expenseActionsStyle} className="expense-actions-cell">
                                                    <div className="expense-actions">
                                                        <Button
                                                            size="sm"
                                                            variant="primary"
                                                            onClick={() => openEditModal(e)}
                                                            title={t('common.edit')}
                                                            aria-label={t('common.edit')}
                                                        >
                                                            <i className="fa-solid fa-pen" aria-hidden="true" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => handleDelete(e.id)}
                                                            title={t('common.delete')}
                                                            aria-label={t('common.delete')}
                                                        >
                                                            <i className="fa-solid fa-trash" aria-hidden="true" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {filteredExpenses.length > 0 && (
                                    <tfoot>
                                        <tr className="fw-semibold">
                                            <td style={expenseCol1Style} />
                                            {visibleColumns.map((col, idx) => (
                                                <td key={col.key} style={columnStyle(col.key)}>
                                                    {idx === 0
                                                        ? t('expenses.totals')
                                                        : EXPENSE_NUMERIC_TOTAL_COLUMNS.has(col.key)
                                                            ? formatEuroForLanguage(totalRow[col.key] || 0)
                                                            : null}
                                                </td>
                                            ))}
                                            <td style={expenseActionsStyle} className="expense-actions-cell" />
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default Expenses;
